import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 7) + "-01";
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [sales, setSales] = useState(null);
  const [stock, setStock] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [appts, setAppts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [purchasesRep, setPurchasesRep] = useState(null);
  const [expensesRep, setExpensesRep] = useState(null);

  const loadSales = () => api.get("/reports/sales", { params: { from_date: fromDate, to_date: toDate } }).then(r => setSales(r.data));
  const loadPurchases = () => api.get("/reports/purchases", { params: { from_date: fromDate, to_date: toDate } }).then(r => setPurchasesRep(r.data));
  const loadExpenses = () => api.get("/reports/expenses", { params: { from_date: fromDate, to_date: toDate } }).then(r => setExpensesRep(r.data));

  useEffect(() => { loadSales(); loadPurchases(); loadExpenses(); }, [fromDate, toDate]); // eslint-disable-line
  useEffect(() => {
    api.get("/products", { params: { low_stock: true } }).then(r => setStock(r.data));
    api.get("/batches").then(r => setExpiring(r.data.filter(b => b.expiry_date && b.quantity > 0).slice(0, 50)));
    api.get("/appointments").then(r => setAppts(r.data));
    api.get("/customers").then(r => setCustomers(r.data));
  }, []);

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Insights</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Reports</h1>
      </div>

      <Tabs defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales" data-testid="rep-sales">Sales</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="rep-purchases">Purchases</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="rep-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="pnl" data-testid="rep-pnl">P&L</TabsTrigger>
          <TabsTrigger value="stock" data-testid="rep-stock">Stock</TabsTrigger>
          <TabsTrigger value="expiry" data-testid="rep-expiry">Expiry</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="rep-appt">Appointments</TabsTrigger>
          <TabsTrigger value="customers" data-testid="rep-cust">Customers</TabsTrigger>
        </TabsList>
        <TabsContent value="sales" className="mt-4">
          <Card className="p-4">
            <div className="flex items-end gap-2">
              <div><Label className="text-xs">From</Label><Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} /></div>
              <Button onClick={loadSales}>Refresh</Button>
            </div>
            {sales && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
                <KPI label="Invoices" v={sales.count} />
                <KPI label="Total" v={`₹${sales.total.toFixed(2)}`} />
                <KPI label="Tax" v={`₹${sales.tax.toFixed(2)}`} />
                <KPI label="Product sales" v={`₹${sales.product_total.toFixed(2)}`} />
                <KPI label="Service sales" v={`₹${sales.service_total.toFixed(2)}`} />
              </div>
            )}
            {sales && (
              <Table className="mt-4">
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {sales.by_day.map(d => (
                    <TableRow key={d.date}><TableCell>{d.date}</TableCell><TableCell className="text-right font-mono">₹{d.total.toFixed(2)}</TableCell></TableRow>
                  ))}
                  {sales.by_day.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-4">No sales in this range.</TableCell></TableRow>}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
        <TabsContent value="purchases" className="mt-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-3">Purchases summary {fromDate} → {toDate}</div>
            {purchasesRep && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KPI label="Purchases" v={purchasesRep.count} />
                <KPI label="Total" v={`₹${purchasesRep.total.toFixed(2)}`} />
                <KPI label="Paid" v={`₹${purchasesRep.paid.toFixed(2)}`} />
                <KPI label="Balance" v={`₹${purchasesRep.balance.toFixed(2)}`} />
              </div>
            )}
          </Card>
        </TabsContent>
        <TabsContent value="expenses" className="mt-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-3">Expenses {fromDate} → {toDate}</div>
            {expensesRep && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  <KPI label="Entries" v={expensesRep.count} />
                  <KPI label="Total" v={`₹${expensesRep.total.toFixed(2)}`} />
                  <KPI label="Top category" v={expensesRep.by_category[0]?.category || "—"} />
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Category</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {expensesRep.by_category.map(r => <TableRow key={r.category}><TableCell>{r.category}</TableCell><TableCell className="text-right font-mono">₹{r.total.toFixed(2)}</TableCell></TableRow>)}
                    {expensesRep.by_category.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-4">No expenses.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </>
            )}
          </Card>
        </TabsContent>
        <TabsContent value="pnl" className="mt-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-3">Quick P&L for {fromDate} → {toDate}</div>
            {sales && purchasesRep && expensesRep && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <KPI label="Sales" v={`₹${sales.total.toFixed(2)}`} />
                  <KPI label="Purchases (COGS proxy)" v={`₹${purchasesRep.total.toFixed(2)}`} />
                  <KPI label="Expenses" v={`₹${expensesRep.total.toFixed(2)}`} />
                  <KPI label="Net" v={`₹${(sales.total - purchasesRep.total - expensesRep.total).toFixed(2)}`} />
                </div>
                <p className="text-xs text-muted-foreground">Note: COGS proxy uses cost of goods purchased in this window (not cost of items sold). For accurate margin, refine in next iteration.</p>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="stock" className="mt-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-3">Low stock items (≤ threshold)</div>
            <Table>
              <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Barcode</TableHead><TableHead className="text-right">Stock</TableHead><TableHead className="text-right">Threshold</TableHead></TableRow></TableHeader>
              <TableBody>
                {stock.map(p => <TableRow key={p.id}><TableCell>{p.name}</TableCell><TableCell className="font-mono text-xs">{p.barcode || "—"}</TableCell><TableCell className="text-right text-orange-600 font-semibold">{p.stock}</TableCell><TableCell className="text-right">{p.low_stock_threshold}</TableCell></TableRow>)}
                {stock.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">All stocked up.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="expiry" className="mt-4">
          <Card className="p-4">
            <Table>
              <TableHeader><TableRow><TableHead>Batch</TableHead><TableHead>Expiry</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
              <TableBody>
                {expiring.map(b => <TableRow key={b.id}><TableCell className="font-mono text-xs">{b.batch_no}</TableCell><TableCell>{b.expiry_date}</TableCell><TableCell className="text-right">{b.quantity}</TableCell></TableRow>)}
                {expiring.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-4">No batches expiring.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="appointments" className="mt-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-3">Recent appointments ({appts.length})</div>
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Customer</TableHead><TableHead>Pet</TableHead><TableHead>Service</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {appts.slice(0, 50).map(a => <TableRow key={a.id}><TableCell>{new Date(a.slot_start).toLocaleString()}</TableCell><TableCell>{a.customer?.name || "—"}</TableCell><TableCell>{a.pet?.name || "—"}</TableCell><TableCell>{a.service?.name || "—"}</TableCell><TableCell className="text-xs">{a.status}</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        <TabsContent value="customers" className="mt-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-3">Total customers: {customers.length}</div>
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Verified</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
              <TableBody>
                {customers.slice(0, 100).map(c => <TableRow key={c.id}><TableCell>{c.name}</TableCell><TableCell className="font-mono text-sm">{c.mobile_masked || c.mobile}</TableCell><TableCell>{c.mobile_verified ? "Yes" : "—"}</TableCell><TableCell className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const KPI = ({ label, v }) => (
  <div className="rounded-lg border border-border p-3">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-heading text-2xl font-black mt-1">{v}</div>
  </div>
);
